// src/oura/components/client/AsksCard.tsx
//
// Questions to the client: every question a pack put to them, grouped by who
// moves next. A reply we owe in a question's thread, questions waiting on the
// client, and what has been answered, folded away. A row is the question and
// 1 fact (its reply-by, or late in rose); a question that needs us adds the
// client's last words. The rest is in the drawer, which the row opens.
//
// What the client started (a change from their Work page, a call) is the
// Requests card beside it (RequestsCard.tsx): the two used to share one
// "Asks" list, and nobody could tell who was asking whom.

import type { ReactNode } from "react";
import { Loader2Icon, MessagesSquareIcon } from "lucide-react";
import { cn } from "../../lib/cn";
import { dayDate } from "../../lib/oura";
import { DOC_STATUS, Dot, FoldCard, GroupFold, LATE_TEXT, ListRow, ShowMore, TYPE, Tag, type StatusStyle, type Tone } from "@oura/ui";
import { calendarLateText, needUsText, whenText } from "@oura/docs";
import { daysSince, type Ask, type AskGroups } from "./asks";
import type { AskSeq, AskTarget } from "./askTargets";

/** Answered questions shown before "Show N more". */
const ANSWERED_SHOWN = 3;

type AskGroup = "needs_us" | "waiting" | "answered";

/** The group heads, in the Documents board's words and colours. */
const GROUP: Record<AskGroup, { title: string; tone: Tone }> = {
    needs_us: { title: DOC_STATUS.needs_us.staff.label, tone: DOC_STATUS.needs_us.staff.tone },
    waiting: { title: DOC_STATUS.waiting_on_client.staff.label, tone: DOC_STATUS.waiting_on_client.staff.tone },
    answered: { title: "Answered", tone: "green" },
};

/** The last thing the client wrote in the thread, or failing that, the last message. */
function lastWords(ask: Ask) {
    const said = (ask.thread?.messages ?? []).filter((m) => m.kind === "message" || m.kind === "suggested_edit");
    return [...said].reverse().find((m) => m.authorKind === "client") ?? said[said.length - 1] ?? null;
}

/**
 * Those words as a line: "Siew Ling: “Yes, from Monday.”" A reply with no
 * words (a photo of the price list, an edit) says what it was, never an
 * empty pair of quotes. Null when there is nothing to say.
 */
function quoteLine(m: NonNullable<ReturnType<typeof lastWords>>): string | null {
    const body = m.body.trim();
    if (body) return `${m.authorName}: \u201c${body}\u201d`;
    if (m.kind === "suggested_edit" || m.suggestedEdit) return `${m.authorName} suggested an edit`;
    if (m.fileName) return `${m.authorName} sent a file`;
    return null;
}

/**
 * The 1 tag a row carries, only when it says more than its group head: a
 * suggested edit or changes requested on a reply we owe, deferred or changes
 * requested on a decision, and a draft pack. Null otherwise.
 */
function askTag(ask: Ask, group: AskGroup): StatusStyle | null {
    const { item, pack } = ask;
    if (pack.status === "draft") return { label: "Draft", tone: "gray" };
    if (group === "needs_us") {
        if (ask.hasEdit) return { label: "Suggested edit", tone: "amber" };
        if (item.decision === "changes_requested") return { label: "Changes requested", tone: "rose" };
    }
    if (group === "answered") {
        if (item.decision === "deferred") return { label: "Deferred", tone: "gray" };
        if (item.decision === "changes_requested") return { label: "Changes requested", tone: "rose" };
    }
    return null;
}

/** Opens a question with the group its row sat in, so the drawer walks that group. */
type OpenAsk = (t: AskTarget, seq: AskSeq) => void;

/** A group's snapshot for the drawer: its rows' keys in the order this card shows them. */
function seqOf(group: AskGroup, asks: Ask[]): AskSeq {
    return { groupId: `asks.${group}`, label: GROUP[group].title, keys: asks.map((a) => `item:${a.item.id}`) };
}

function AskRow({ ask, group, seq, onOpen }: { ask: Ask; group: AskGroup; seq: AskSeq; onOpen: OpenAsk }) {
    const { item, pack } = ask;
    const last = group === "needs_us" ? lastWords(ask) : null;
    const quote = last && quoteLine(last);

    // The 1 fact: when the client should reply (or how long it has waited),
    // when they last wrote, or when it was decided. Late replaces it in rose.
    let meta: ReactNode = null;
    let late: ReactNode = null;
    if (group === "waiting") {
        late = calendarLateText(item.lateDays);
        const waited = item.replyBy ? null : daysSince(pack.publishedAt);
        meta = item.replyBy
            ? dayDate(item.replyBy) && `Reply by ${dayDate(item.replyBy)}`
            : waited !== null && waited > 0 && `Waiting ${waited} ${waited === 1 ? "day" : "days"}`;
    } else if (group === "needs_us") {
        meta = last && whenText(last.at);
    } else {
        meta = dayDate(item.decidedAt);
    }

    const tag = askTag(ask, group);
    const tags = (ask.fresh || tag) && (
        <>
            {ask.fresh && <Tag tone="blue">New</Tag>}
            {tag && <Tag tone={tag.tone}>{tag.label}</Tag>}
        </>
    );
    return (
        <ListRow
            lead="dot"
            stackMeta
            onClick={() => onOpen({ kind: "item", itemId: item.id }, seq)}
            data-ask-id={item.id}
            icon={<Dot tone={GROUP[group].tone} />}
            title={item.title}
            srStatus={GROUP[group].title}
            sub={quote && (
                <span title={quote} className="block truncate text-gray-600">
                    {quote}
                </span>
            )}
            tags={tags || undefined}
            meta={meta || undefined}
            late={late || undefined}
        />
    );
}

export function QuestionsCard({
    groups,
    loading,
    failed = false,
    onOpen,
}: {
    groups: AskGroups;
    loading: boolean;
    /** The packs could not be read: an empty list here is unknown, never "No questions sent". */
    failed?: boolean;
    onOpen: OpenAsk;
}) {
    const ours = groups.needsUs.length;
    const shown = groups.answered.slice(0, ANSWERED_SHOWN);
    const more = groups.answered.slice(ANSWERED_SHOWN);
    const total = ours + groups.waiting.length + groups.answered.length;
    const seqs = { needs_us: seqOf("needs_us", groups.needsUs), waiting: seqOf("waiting", groups.waiting), answered: seqOf("answered", groups.answered) };
    // The head counts questions still waiting on the client, so it shows only
    // once the packs are read and something is in the card: never a 0 for
    // packs that could not be read. Not "sent this month": Oura sends no new
    // questions (YW, 29 Sep 2026), so that would read 0 beside 4 waiting.
    const headCounts = !loading && !failed && total > 0;
    const late = groups.waiting.filter((a) => (a.item.lateDays ?? 0) > 0).length;
    // "4 out · 2 late": the late ones are some of those out.
    const foldedParts = [
        ours > 0 && needUsText(ours),
        groups.waiting.length > 0 && `${groups.waiting.length} out`,
        late > 0 && <span className={LATE_TEXT}>{late} late</span>,
    ].filter(Boolean);

    return (
        <FoldCard
            // The old "Asks" card's id, so a fold chosen before the rename holds.
            id="asks"
            title="Questions to the client"
            icon={{ icon: MessagesSquareIcon, area: "asks" }}
            right={headCounts && groups.waiting.length > 0 && <span className="whitespace-nowrap tabular-nums">{groups.waiting.length} waiting</span>}
            foldedFact={
                headCounts && (
                    <span className="tabular-nums">
                        {foldedParts.length === 0
                            ? "Nothing out"
                            : foldedParts.map((p, i) => (
                                <span key={i}>
                                    {i > 0 && " · "}
                                    {p}
                                </span>
                            ))}
                    </span>
                )
            }
        >
            {loading ? (
                <p className={cn(TYPE.tb, "m-0 flex items-center gap-2 text-gray-400")}>
                    <Loader2Icon aria-hidden size={16} className="animate-spin" /> Loading
                </p>
            ) : total === 0 && failed ? (
                <p className={cn(TYPE.tb, "m-0 text-gray-500")}>Not read yet. The questions show once Oura can read them.</p>
            ) : total === 0 ? (
                <p className={cn(TYPE.tb, "m-0 text-gray-500")}>No questions sent to the client yet.</p>
            ) : (
                // The groups are the only children of this box, so the first loses its divider.
                <div>
                    {ours > 0 && (
                        <GroupFold id="asks.needs_us" lead="dot" tone={GROUP.needs_us.tone} title={GROUP.needs_us.title} count={ours}>
                            <div>
                                {groups.needsUs.map((a) => <AskRow key={a.item.id} ask={a} group="needs_us" seq={seqs.needs_us} onOpen={onOpen} />)}
                            </div>
                        </GroupFold>
                    )}
                    {groups.waiting.length > 0 && (
                        <GroupFold
                            id="asks.waiting"
                            lead="dot"
                            tone={GROUP.waiting.tone}
                            title={GROUP.waiting.title}
                            count={groups.waiting.length}
                            note={late > 0 && <span className={LATE_TEXT}>{late} late</span>}
                        >
                            <div>
                                {groups.waiting.map((a) => <AskRow key={a.item.id} ask={a} group="waiting" seq={seqs.waiting} onOpen={onOpen} />)}
                            </div>
                        </GroupFold>
                    )}
                    {groups.answered.length > 0 && (
                        <GroupFold id="asks.answered" lead="dot" tone={GROUP.answered.tone} title={GROUP.answered.title} count={groups.answered.length} defaultFolded>
                            <div>
                                {shown.map((a) => <AskRow key={a.item.id} ask={a} group="answered" seq={seqs.answered} onOpen={onOpen} />)}
                            </div>
                            <ShowMore count={more.length}>
                                {more.map((a) => <AskRow key={a.item.id} ask={a} group="answered" seq={seqs.answered} onOpen={onOpen} />)}
                            </ShowMore>
                        </GroupFold>
                    )}
                </div>
            )}
        </FoldCard>
    );
}
